import { useTargetingContext } from '@openx/components-targeting/utils/context/TargetingContext';
import type { DealInsightsCreateJobIdParamsApi, DealInsightsFilterOptions } from '@openx/types/dealInsights';
import { createContext, type ReactNode, useContext, useMemo, useState } from 'react';
import { useDealInsigthsInitialState } from '../../DealInsights/hooks/useDealInsigthsInitialState';
import type { DealInsightsDateRange } from '../../DealInsights/types';
import { getDealInsightsSupportedDateRange } from '../../DealInsights/utils/getDealInsightsSupportedDateRange';
import { filtersToApiParams } from '../../utils/dealInsightsApiUtils';

export type DealInsightsV2QueryParams = Omit<DealInsightsCreateJobIdParamsApi, 'charts'>;

type DealInsightsV2ContextType = {
  dateRange: DealInsightsDateRange;
  filters: DealInsightsFilterOptions;
  setDateRange: (dateRange: DealInsightsDateRange) => void;
  setFilters: (filters: DealInsightsFilterOptions) => void;
  /** The only thing sections need: account, dates and filters mapped to the API params of every section job. */
  queryParams: DealInsightsV2QueryParams;
};

const DealInsightsV2Context = createContext<DealInsightsV2ContextType | undefined>(undefined);

export const DealInsightsV2Provider = ({ children }: { children: ReactNode }) => {
  const initialState = useDealInsigthsInitialState();
  const accountUid = useTargetingContext().useAccountUid();

  const [dateRange, setDateRange] = useState<DealInsightsDateRange>(initialState.dateRange);
  const [filters, setFilters] = useState<DealInsightsFilterOptions>(initialState.filters);

  const queryParams = useMemo(
    () =>
      filtersToApiParams({
        startDate: dateRange.startDate,
        // computed inside the memo: maxDate is a new Date on every call and would defeat memoization
        endDate: dateRange.endDate ?? getDealInsightsSupportedDateRange().maxDate,
        account: accountUid,
        ...filters,
      }),
    [dateRange, filters, accountUid]
  );

  const value = useMemo(
    () => ({ dateRange, filters, setDateRange, setFilters, queryParams }),
    [dateRange, filters, queryParams]
  );

  return <DealInsightsV2Context.Provider value={value}>{children}</DealInsightsV2Context.Provider>;
};

export const useDealInsightsV2Context = () => {
  const context = useContext(DealInsightsV2Context);

  if (!context) {
    throw new Error('useDealInsightsV2Context must be used within a DealInsightsV2Provider');
  }

  return context;
};
